import { inject, Pipe, type PipeTransform } from '@angular/core';
import { type ValidationErrors } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { VALIDATION_MESSAGE_RULES } from './validation-message.pipe';

/**
 * Impure pipe that resolves the first validation error to a translated message.
 * Uses the `validations` nested key in the global transloco scope.
 *
 * @example
 * ```html
 * <hlm-field-error>
 *   {{ signalForm.get('name')?.errors | validationMessage }}
 * </hlm-field-error>
 * ```
 */
@Pipe({ name: 'validationMessage', pure: false })
export class ValidationMessageLegacyPipe implements PipeTransform {
  private readonly transloco = inject(TranslocoService);
  private readonly rules = inject(VALIDATION_MESSAGE_RULES);

  private lastErrors: ValidationErrors | null = null;
  private lastLang = '';
  private lastMessage = '';

  transform(errors: ValidationErrors | null | undefined): string {
    if (!errors) {
      this.lastMessage = '';
      this.lastErrors = null;
      return '';
    }

    const lang = this.transloco.getActiveLang();
    if (errors === this.lastErrors && lang === this.lastLang) return this.lastMessage;
    this.lastErrors = errors;
    this.lastLang = lang;

    const firstKey = Object.keys(errors)[0];
    const rule = this.rules[firstKey];

    if (rule) {
      const params = rule.params ? rule.params(errors[firstKey]) : {};
      this.lastMessage = this.transloco.translate(rule.key, params);
    } else {
      this.lastMessage = this.transloco.translate('Validations.Default');
    }

    return this.lastMessage;
  }
}
